// fetch with the part of axios the store site used, so the browser code needs no HTTP library.
// post() resolves { status, data, headers, config } on a 2xx and rejects otherwise with an Error
// that carries response = { status, data, headers }, as axios did. A timeout rejects with
// code ECONNABORTED and a network failure with code ERR_NETWORK, both without a response.

export interface IHttpRequestConfig {
  url: string;
  method: string;
  // The request body as sent, a string for JSON bodies.
  data?: string;
}

export interface IHttpResponse<T = any> {
  status: number;
  data: T;
  headers: Record<string, string>;
  config: IHttpRequestConfig;
}

export interface IHttpError extends Error {
  code?: string;
  response?: {
    status: number;
    data: any;
    headers: Record<string, string>;
  };
}

export interface IHttpPostOptions {
  headers?: Record<string, string>;
  timeout?: number;
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  const wanted = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === wanted);
}

function parseBody(text: string): any {
  if (!text) {
    return text;
  }

  try {
    return JSON.parse(text);
  } catch (err) {
    return text;
  }
}

function httpError(
  message: string,
  code?: string,
  response?: IHttpError['response']
): IHttpError {
  const error: IHttpError = new Error(message);
  error.code = code;
  error.response = response;
  return error;
}

export async function post<T = any>(
  url: string,
  data?: unknown,
  options?: IHttpPostOptions
): Promise<IHttpResponse<T>> {
  options = options || {};
  const headers: Record<string, string> = {
    Accept: 'application/json, text/plain, */*',
    ...(options.headers || {}),
  };

  let body: BodyInit | undefined;
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    // The browser sets the multipart Content-Type with its boundary.
    body = data;
  } else if (typeof data === 'string') {
    body = data;
  } else if (typeof data !== 'undefined' && data !== null) {
    body = JSON.stringify(data);
    if (!hasHeader(headers, 'Content-Type')) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const config: IHttpRequestConfig = {
    url,
    method: 'POST',
    data: typeof body === 'string' ? body : undefined,
  };

  const controller =
    options.timeout && typeof AbortController !== 'undefined'
      ? new AbortController()
      : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), options.timeout)
    : null;

  let response: Response;
  let text: string;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body,
      signal: controller ? controller.signal : undefined,
    });
    text = await response.text();
  } catch (err) {
    if (controller && controller.signal.aborted) {
      throw httpError(`timeout of ${options.timeout}ms exceeded`, 'ECONNABORTED');
    }
    throw httpError('Network Error', 'ERR_NETWORK');
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }

  const responseHeaders: Record<string, string> = {};
  response.headers.forEach((value, name) => {
    responseHeaders[name] = value;
  });

  const result: IHttpResponse<T> = {
    status: response.status,
    data: parseBody(text),
    headers: responseHeaders,
    config,
  };

  if (response.status < 200 || response.status >= 300) {
    throw httpError(
      `Request failed with status code ${response.status}`,
      'ERR_BAD_RESPONSE',
      { status: result.status, data: result.data, headers: result.headers }
    );
  }

  return result;
}

export interface IHttpClient {
  post<T = any>(
    path: string,
    data?: unknown,
    options?: IHttpPostOptions
  ): Promise<IHttpResponse<T>>;
}

// A client bound to a base URL and default headers, like axios.create({ baseURL, headers }).
export function createHttpClient(defaults: {
  baseURL: string;
  headers?: Record<string, string>;
}): IHttpClient {
  return {
    post: (path, data, options) =>
      post(`${defaults.baseURL}${path}`, data, {
        ...(options || {}),
        headers: { ...(defaults.headers || {}), ...((options && options.headers) || {}) },
      }),
  };
}

const http = { post };

export default http;
